import React, { useContext, useMemo, useState } from 'react';
import keyBy from 'lodash/keyBy';

import { IShoppingList, IShoppingListItem, IShoppingCategory } from '@components/pages/Room/Shop/model/ShoppingList';
import ProductCard from '@components/shared/cards/ProductCard';
import isDoNotUseCommissionTier from '@lib/vendorVariant/isDoNotUseCommissionTier';
import useHolidaySale from '@hooks/useHolidaySale/useHolidaySale';
import UserContext from '@context/User/user-context';
import FindYourStyle from '@components/shared/CTACards/FindYourStyle';
import WantThisLook from '@components/shared/CTACards/WantThisLook';
import HolidaySaleCTA from '@components/shared/CTACards/HolidaySaleCTA';
import ConversionEmailHarvesterCard from '@components/shared/ConversionEmailHarvester/DiscoverFeedCard/ConversionEmailHarvesterCard';
import HolidaySale from '@hooks/useHolidaySale/HolidaySale';
import ReadMore from '@components/shared/ReadMore/ReadMore';
import { useTheme } from '@components/theme';
import classnames from 'classnames';
import DesignerPicksCTA from '@components/shared/CTACards/DesignerPicksCTA';
import { useMinBreakpoint } from '@hooks/useMediaQuery/useMediaQuery';
import breakpoints from '@constants/breakpoints';
import { commissionTiersById } from '@constants/commissionTiers';
import styles from './BoardItemList.module.scss';

interface IBoardItemListProps {
  boardId: string;
  shoppingList: IShoppingList;
  onSave: (product: any) => void;
  readOnly: boolean;
  savedVendorVariantIds: {[vendorVariantId: string]: boolean};
  setQuickViewModalContent: (product: any) => void;
  hasDescription?: boolean;
}

enum CTACategories {
  EMAIL_HARVESTER = 'EMAIL_HARVESTER',
  HOLIDAY_SALE = 'HOLIDAY_SALE',
  WANT_THIS_LOOK = 'WANT_THIS_LOOK',
  FIND_YOUR_STYLE = 'FIND_YOUR_STYLE',
  DESIGNER_PICKS = 'DESIGNER_PICKS',
}

export default function BoardItemList(props: IBoardItemListProps) {
  const {
    hasDescription,
    shoppingList
  } = props;
  const { categories: unfilteredCategories } = shoppingList;
  const categories = useMemo(() => {
    return unfilteredCategories
      .map((category) => ({
        ...category,
        products: category.products.filter(filterShoppingListItem)
      }))
      .filter((category) => category.products.length > 0);
  }, [unfilteredCategories]);

  const { user } = useContext(UserContext);
  const holidaySale = useHolidaySale();
  const categoriesCount = categories.length;
  let isHolidaySaleSet = false;

  let index = 0;

  const getCTAComponent = (category: IShoppingCategory): string | null => {
    // eslint-disable-next-line no-plusplus
    index++;

    if (index === 2) {
      return CTACategories.DESIGNER_PICKS;
    }

    if (categories[0] === category) {
      if (holidaySale.isActive()) {
        isHolidaySaleSet = true;
        return CTACategories.HOLIDAY_SALE;
      }

      if (!user) {
        return CTACategories.EMAIL_HARVESTER;
      }

      return CTACategories.WANT_THIS_LOOK;
    }

    if (categoriesCount === 1) return null;

    if (index === categoriesCount) {
      if (holidaySale.isActive() && !isHolidaySaleSet) {
        return CTACategories.HOLIDAY_SALE;
      } else {
        return user ? CTACategories.WANT_THIS_LOOK : CTACategories.FIND_YOUR_STYLE;
      }
    }

    return null;
  };

  const categoriesByPrimaryName = useMemo(() => keyBy(categories, 'primaryName'), [categories]);
  return (
    <div className={ styles.CategoryList }>
      {categories.map((category) => (
        <React.Fragment key={category.primaryName}>
          <hr className={hasDescription ? styles.HideHRIfMobile : ''} />
          <BoardItemListCategory
            {...props}
            category={categoriesByPrimaryName[category.primaryName]}
            shoppingListItems={category.products}
            CTACategory={getCTAComponent(category)}
            holidaySale={holidaySale}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

interface IBoardItemListCategory {
  boardId: string;
  onSave: (product: any) => void;
  readOnly: boolean;
  savedVendorVariantIds: {[vendorVariantId: string]: boolean};
  setQuickViewModalContent: (product: any) => void;
  hasDescription?: boolean;
  CTACategory: string | null;
  holidaySale: HolidaySale;
  shoppingListItems: IShoppingListItem[];
  category: IShoppingCategory;
}

function BoardItemListCategory(props: IBoardItemListCategory) {
  const {
    boardId,
    category,
    shoppingListItems,
    onSave,
    readOnly,
    CTACategory,
    holidaySale,
    savedVendorVariantIds,
    setQuickViewModalContent
  } = props;
  const [expanded, setExpanded] = useState(false);

  const theme = useTheme();

  const isDesktop = useMinBreakpoint(breakpoints.desktop);

  return (
    <div>
      <a
        className={ classnames(theme.styles.Title1, styles.Title) }
        href={getShopHref(category)}
        target="_blank"
        rel="noreferrer"
      >
        {category.primaryName}
      </a>
      {
        !!category.readMore && (
          <ReadMore
            className={classnames(styles.ReadMore, theme.styles.Body)}
            expanded={expanded}
            setExpanded={setExpanded}
          >
            <p>{category.readMore}&nbsp;
              <a
                href={category.discoverUrl}
                target="_blank"
                rel="noreferrer"
              >
                {category.discover}
              </a>
            </p>
          </ReadMore>
        )
      }
      <div className={ styles.ShoppingListContainer }>
        {shoppingListItems.map((sli: IShoppingListItem) => {
          return (
            <ProductCard
              className={ styles.ProductCard }
              key={sli.vendorVariant.id}
              vendorVariant={sli.vendorVariant}
              onSave={ onSave }
              readOnly={ readOnly }
              saved={ savedVendorVariantIds[sli.vendorVariant.id] }
              setQuickViewModalContent={ setQuickViewModalContent }
            />
          );
        })}

        {
          (CTACategory === CTACategories.EMAIL_HARVESTER) && (
            <div className={styles.CTAContainer}>
              <ConversionEmailHarvesterCard
                className={ `${styles.CTACard} `}
                boardId={ boardId }
                useAlternateLayout={isDesktop}
              />
            </div>
          )
        }

        {
          (CTACategory === CTACategories.HOLIDAY_SALE) && (
            <div className={styles.CTAContainer}>
              <HolidaySaleCTA size={'1'} sale={holidaySale} className={ `${styles.CTACard} ${styles.HolidaySaleCTACard}` } isInsideContainer />
            </div>
          )
        }

        {
          (CTACategory === CTACategories.FIND_YOUR_STYLE) && (
            <div className={styles.CTAContainer}>
              <WantThisLook
                size={'1'}
                className={`${styles.CTACard}`}
                primaryTextClassName={styles.PrimaryText}
                secondaryTextClassName={styles.SecondaryText}
              />
            </div>
          )
        }

        {
          (CTACategory === CTACategories.WANT_THIS_LOOK) && (
            <div className={styles.CTAContainer}>
              <FindYourStyle
                size={'1'}
                className={classnames(
                  styles.CTACard,
                  styles.CTAIconOverride,
                )}
                primaryTextClassName={styles.PrimaryText}
                secondaryTextClassName={styles.SecondaryText}
              />
            </div>
          )
        }
      </div>

      {
        (CTACategory === CTACategories.DESIGNER_PICKS) && (
          <DesignerPicksCTA />
        )
      }
    </div>
  );
}

function filterShoppingListItem(shoppingListItem: IShoppingListItem) {
  const {
    vendorVariant: vv
  } = shoppingListItem;
  return !!vv &&
    !!vv.image &&
    !!vv.retailPrice &&
    !!vv.commissionTier?.id &&
    !isDoNotUseCommissionTier(vv.commissionTier?.id) &&
    !isNotRecommendedCommissionTier(vv.commissionTier?.id) &&
    !vv.vendor?.doNotUse;
}

function getShopHref(category: IShoppingCategory) {
  const shopHref = '/shop';
  const slug = category.topLevelTaxonomyCategory?.slug;
  if (slug) {
    return `${shopHref}/${slug}`;
  }

  return shopHref;
}

function isNotRecommendedCommissionTier(
  commissionTierId: string | number,
): boolean {
  if (!commissionTierId) {
    return true;
  }
  const _commissionTierId = typeof commissionTierId === 'string' ? parseInt(commissionTierId, 10) : commissionTierId;
  return _commissionTierId === commissionTiersById.NOT_RECOMMENDED;
}
